import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import formatMoney from 'utils/formatters/Money';
import { useMutation } from '@tanstack/react-query';
import { rerateCosts, getShipment } from 'utils/ShipmentWebAPIUtils';
import { BadgeAlt as Badge, Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Checkbox, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Skeleton, useToast, } from '@cargomatic/ui';
import { ChevronRight, ChevronDown, X, Info, ExternalLink, MoreHorizontal, Edit, CheckCheck, } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import { useOrderRTBApLineItems } from './hooks/useOrderRTBLineItems';
import ShipmentExternalLink from './ShipmentExternalLink';
import CarrierRateCardLink from './CarrierRateCardLink';
import OrderVendorPayables from './OrderVendorPayables';
import { formatDate } from 'utils/formatters/Date';
import { humanize, titleize } from 'underscore.string';
import { removeLineItem, approveCarrierCost, } from 'utils/AccountingWebAPIUtils';
import CargomaticModal from 'components/v2/modal';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { isApproveDisabled, canShowApproveAction, canShowEditAction, canShowDeleteAction, } from 'helpers/accounting';
import SessionStore from 'stores/SessionStore';
import { NO_DATA } from './constants';
import GeneratePayoutDialog from './GeneratePayoutDialog';
import { RerateCostsDialog } from './RerateCostsDialog';
export function APChargesTable({ orderId, carrierId, onEditCharge, }) {
    var _a;
    const { toast } = useToast();
    const isFinance = SessionStore.isFinance() || SessionStore.isFinanceAdmin();
    // Fetch AP line items
    const { data: apLines = [], isLoading: isLoadingAP, refetch, } = useOrderRTBApLineItems(orderId);
    // Internal state
    const [apFilter, setApFilter] = useState('all');
    const [showOptionalColumns, setShowOptionalColumns] = useState(false);
    const [selectedCharges, setSelectedCharges] = useState(new Set());
    const [showPayoutDetailsModal, setShowPayoutDetailsModal] = useState(false);
    const [selectedPayoutCharge, setSelectedPayoutCharge] = useState(null);
    const [showConfirmVoidModal, setShowConfirmVoidModal] = useState(false);
    const [showConfirmApproveModal, setShowConfirmApproveModal] = useState(false);
    const [chargeToVoid, setChargeToVoid] = useState(null);
    const [chargeToApprove, setChargeToApprove] = useState(null);
    const [voidPending, setVoidPending] = useState(false);
    const [approvePending, setApprovePending] = useState(false);
    // Payout generation state
    const [payoutModalOpen, setPayoutModalOpen] = useState(false);
    // Derived data
    const filteredCharges = useMemo(() => {
        if (apFilter === 'all')
            return apLines;
        if (apFilter === 'actioned') {
            return apLines.filter((c) => { var _a; return (_a = c.payout_id) === null || _a === void 0 ? void 0 : _a.number; });
        }
        return apLines.filter((c) => { var _a; return !((_a = c.payout_id) === null || _a === void 0 ? void 0 : _a.number); });
    }, [apLines, apFilter]);
    const filteredTotal = useMemo(() => {
        return filteredCharges.reduce((sum, c) => sum + (c.value || 0), 0);
    }, [filteredCharges]);
    const unactionedCharges = useMemo(() => {
        return apLines.filter((c) => { var _a; return !((_a = c.payout_id) === null || _a === void 0 ? void 0 : _a.number); });
    }, [apLines]);
    const allChargesActioned = unactionedCharges.length === 0;
    const allUnactionedSelected = useMemo(() => {
        if (unactionedCharges.length === 0)
            return false;
        return unactionedCharges.every((c) => selectedCharges.has(c._id));
    }, [unactionedCharges, selectedCharges]);
    // Handlers
    const handleSelectAll = useCallback((checked) => {
        if (checked) {
            const newSet = new Set(selectedCharges);
            unactionedCharges.forEach((c) => newSet.add(c._id));
            setSelectedCharges(newSet);
        }
        else {
            const newSet = new Set(selectedCharges);
            unactionedCharges.forEach((c) => newSet.delete(c._id));
            setSelectedCharges(newSet);
        }
    }, [unactionedCharges, selectedCharges]);
    const handleSelectCharge = useCallback((chargeId, checked) => {
        const newSet = new Set(selectedCharges);
        if (checked) {
            newSet.add(chargeId);
        }
        else {
            newSet.delete(chargeId);
        }
        setSelectedCharges(newSet);
    }, [selectedCharges]);
    const handleInitiateVoid = useCallback((charge) => {
        setChargeToVoid(charge);
        setShowConfirmVoidModal(true);
    }, []);
    const handleConfirmVoid = useCallback(async () => {
        var _a;
        if (!((_a = chargeToVoid === null || chargeToVoid === void 0 ? void 0 : chargeToVoid.shipment) === null || _a === void 0 ? void 0 : _a._id) || !(chargeToVoid === null || chargeToVoid === void 0 ? void 0 : chargeToVoid.adjustment_id))
            return;
        setVoidPending(true);
        try {
            await removeLineItem(chargeToVoid.adjustment_id, 'payables', chargeToVoid.shipment._id);
            toast({
                title: 'Charge Voided',
                description: 'The charge has been successfully voided.',
            });
            refetch();
        }
        catch (_b) {
            toast({
                title: 'Error',
                description: 'Failed to void the charge. Please try again.',
                variant: 'destructive',
            });
        }
        finally {
            setVoidPending(false);
            setShowConfirmVoidModal(false);
            setChargeToVoid(null);
        }
    }, [chargeToVoid, toast, refetch]);
    const handleInitiateApprove = useCallback((charge) => {
        setChargeToApprove(charge);
        setShowConfirmApproveModal(true);
    }, []);
    const handleConfirmApprove = useCallback(async () => {
        var _a;
        if (!((_a = chargeToApprove === null || chargeToApprove === void 0 ? void 0 : chargeToApprove.shipment) === null || _a === void 0 ? void 0 : _a._id) || !(chargeToApprove === null || chargeToApprove === void 0 ? void 0 : chargeToApprove.adjustment_id))
            return;
        setApprovePending(true);
        try {
            await approveCarrierCost(chargeToApprove.shipment._id, chargeToApprove.adjustment_id);
            toast({
                title: 'Charge Approved',
                description: 'The charge has been approved.',
            });
            refetch();
        }
        catch (_b) {
            toast({
                title: 'Error',
                description: 'Failed to approve the charge. Please try again.',
                variant: 'destructive',
            });
        }
        finally {
            setApprovePending(false);
            setShowConfirmApproveModal(false);
            setChargeToApprove(null);
        }
    }, [chargeToApprove, toast, refetch]);
    const createPayoutDisabled = selectedCharges.size === 0 || allChargesActioned;
    const createPayoutDisabledReason = allChargesActioned
        ? 'All charge lines have already been paid out.'
        : selectedCharges.size === 0
            ? 'No charges selected'
            : undefined;
    const handleCreatePayout = useCallback(() => {
        if (!orderId)
            return;
        setPayoutModalOpen(true);
    }, [orderId]);
    const handlePayoutSuccess = useCallback(() => {
        setSelectedCharges(new Set());
        setPayoutModalOpen(false);
        refetch();
    }, [refetch]);
    const handleViewPayout = useCallback((charge) => {
        setSelectedPayoutCharge(charge);
        setShowPayoutDetailsModal(true);
    }, []);
    // Rerate state & logic
    const [showRerateDialog, setShowRerateDialog] = useState(false);
    const AP_RERATE_ELIGIBLE_TYPES = ['carrier_cost', 'fuel_surcharge'];
    const selectedChargeObjects = useMemo(() => {
        return apLines.filter((c) => selectedCharges.has(c._id));
    }, [apLines, selectedCharges]);
    const isRerateEnabled = useMemo(() => {
        if (selectedChargeObjects.length === 0)
            return false;
        return selectedChargeObjects.every((c) => { var _a; return AP_RERATE_ELIGIBLE_TYPES.includes((_a = c.type) !== null && _a !== void 0 ? _a : ''); });
    }, [selectedChargeObjects]);
    const rerateEligibleCharges = useMemo(() => {
        return selectedChargeObjects.filter((c) => { var _a; return AP_RERATE_ELIGIBLE_TYPES.includes((_a = c.type) !== null && _a !== void 0 ? _a : ''); });
    }, [selectedChargeObjects]);
    const rerateCharges = useCallback(async (chargeIds) => {
        var _a;
        const selectedItems = rerateEligibleCharges.filter((c) => chargeIds.includes(c._id));
        const costTypesByShipment = new Map();
        for (const item of selectedItems) {
            const shipId = (_a = item.shipment) === null || _a === void 0 ? void 0 : _a._id;
            if (!shipId)
                continue;
            if (!costTypesByShipment.has(shipId))
                costTypesByShipment.set(shipId, new Set());
            if (item.type === 'carrier_cost')
                costTypesByShipment.get(shipId).add('carrier');
            if (item.type === 'fuel_surcharge')
                costTypesByShipment.get(shipId).add('carrier_fsc');
        }
        const shipmentIds = Array.from(costTypesByShipment.keys());
        const shipmentResponses = await Promise.all(shipmentIds.map((id) => getShipment(id)));
        const shipmentsById = new Map();
        shipmentIds.forEach((id, index) => {
            var _a, _b;
            const data = shipmentResponses[index];
            shipmentsById.set(id, (_b = (_a = data.entity) === null || _a === void 0 ? void 0 : _a.item) !== null && _b !== void 0 ? _b : data.entity);
        });
        await Promise.all(Array.from(costTypesByShipment.entries()).map(([shipmentId, costTypes]) => {
            const shipment = shipmentsById.get(shipmentId);
            return rerateCosts(shipment, Array.from(costTypes));
        }));
    }, [rerateEligibleCharges]);
    const rerateMutation = useMutation({
        mutationFn: rerateCharges,
        onSuccess: () => {
            toast({
                title: 'Rerate Successful',
                description: 'Costs have been rerated successfully.',
            });
            setSelectedCharges(new Set());
            setShowRerateDialog(false);
            refetch();
        },
        onError: (err) => {
            var _a, _b;
            const message = (_b = (_a = err === null || err === void 0 ? void 0 : err.entity) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'An error occurred.';
            toast({
                title: 'Rerate Failed',
                description: message,
                variant: 'destructive',
            });
        },
    });
    const handleRerate = useCallback((chargeIds) => {
        rerateMutation.mutate(chargeIds);
    }, [rerateMutation]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "flex items-center justify-between mb-4" },
            React.createElement("div", { className: "flex items-center gap-4" },
                React.createElement("h2", { className: "text-lg font-semibold" }, "Accounts Payable"),
                React.createElement(Select, { value: apFilter, onValueChange: (value) => setApFilter(value) },
                    React.createElement(SelectTrigger, { className: "w-[220px]", onClick: (e) => e.stopPropagation() },
                        React.createElement(SelectValue, null)),
                    React.createElement(SelectContent, null,
                        React.createElement(SelectItem, { value: "all" }, "All Payables"),
                        React.createElement(SelectItem, { value: "actioned" }, "Actioned Payables"),
                        React.createElement(SelectItem, { value: "unactioned" }, "Unactioned Pending Payables")))),
            React.createElement("div", { className: "flex items-center gap-4" },
                React.createElement(DropdownMenu, null,
                    React.createElement(DropdownMenuTrigger, { asChild: true },
                        React.createElement(Button, { variant: "outline", size: "sm", className: "border-zinc-300 text-zinc-700 hover:bg-zinc-50 bg-transparent whitespace-nowrap" },
                            "Actions",
                            React.createElement(ChevronDown, { className: "ml-1 h-4 w-4" }))),
                    React.createElement(DropdownMenuContent, { align: "end" },
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement(DropdownMenuItem, { onClick: (e) => {
                                            if (createPayoutDisabled) {
                                                e.preventDefault();
                                                return;
                                            }
                                            handleCreatePayout();
                                        }, className: createPayoutDisabled
                                            ? 'opacity-50 cursor-not-allowed'
                                            : '', onSelect: createPayoutDisabled
                                            ? (e) => e.preventDefault()
                                            : undefined }, "Create Payout")),
                                createPayoutDisabledReason && (React.createElement(TooltipContent, { side: "left" }, createPayoutDisabledReason)))),
                        React.createElement(DropdownMenuItem, { onClick: () => setShowRerateDialog(true), disabled: !isRerateEnabled }, "Rerate Costs"))))),
        React.createElement(CarrierRateCardLink, { orderId: orderId, carrierId: carrierId }),
        React.createElement(Table, null,
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null,
                    React.createElement(TableHead, { className: "w-12" }, allChargesActioned || apFilter === 'actioned' ? (React.createElement(TooltipProvider, null,
                        React.createElement(Tooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement("span", { className: "inline-flex" },
                                    React.createElement(Checkbox, { checked: false, disabled: true }))),
                            React.createElement(TooltipContent, null, "All charge lines have already been paid out.")))) : (React.createElement(Checkbox, { checked: allUnactionedSelected, onCheckedChange: handleSelectAll, disabled: unactionedCharges.length === 0 }))),
                    React.createElement(TableHead, { className: "text-left whitespace-nowrap" }, "Charge"),
                    React.createElement(TableHead, { className: "text-right whitespace-nowrap" }, "Cost"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Shipment"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Equipment"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Entered Date"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Rate Source"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Approved Payout"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Approved Payout Date"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Payout Status"),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "Paid Date"),
                    showOptionalColumns && (React.createElement(React.Fragment, null,
                        React.createElement(TableHead, null, "Reason Code"),
                        React.createElement(TableHead, null, "Notes"),
                        React.createElement(TableHead, null, "Last User"))),
                    React.createElement(TableHead, { className: "w-12" },
                        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0", onClick: () => setShowOptionalColumns(!showOptionalColumns), title: showOptionalColumns
                                ? 'Hide optional columns'
                                : 'Show optional columns' },
                            React.createElement(ChevronRight, { className: cn('h-4 w-4 transition-transform', showOptionalColumns && 'rotate-90') }))),
                    React.createElement(TableHead, null, "Actions"))),
            React.createElement(TableBody, null,
                isLoadingAP
                    ? Array.from({ length: 5 }).map((_, i) => (React.createElement(TableRow, { key: `ap-skel-${i}` },
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-4 rounded" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-32" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-16 ml-auto" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-20" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-20" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        showOptionalColumns && (React.createElement(React.Fragment, null,
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-20" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-28" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-20" })))),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-4" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-6 w-16" })))))
                    : filteredCharges.map((charge) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
                        const isUnactioned = !((_a = charge.payout_id) === null || _a === void 0 ? void 0 : _a.number);
                        return (React.createElement(TableRow, { key: charge._id },
                            React.createElement(TableCell, null, allChargesActioned || apFilter === 'actioned' ? (React.createElement(TooltipProvider, null,
                                React.createElement(Tooltip, null,
                                    React.createElement(TooltipTrigger, { asChild: true },
                                        React.createElement("span", { className: "inline-flex" },
                                            React.createElement(Checkbox, { checked: false, disabled: true }))),
                                    React.createElement(TooltipContent, null, "All charge lines have already been paid out.")))) : isUnactioned ? (React.createElement(Checkbox, { checked: selectedCharges.has(charge._id), onCheckedChange: (checked) => handleSelectCharge(charge._id, checked) })) : (React.createElement(TooltipProvider, null,
                                React.createElement(Tooltip, null,
                                    React.createElement(TooltipTrigger, { asChild: true },
                                        React.createElement("span", { className: "inline-flex" },
                                            React.createElement(Checkbox, { checked: false, disabled: true }))),
                                    React.createElement(TooltipContent, null, "This charge line has already been paid out."))))),
                            React.createElement(TableCell, { className: "whitespace-nowrap" },
                                React.createElement("div", { className: "flex items-center gap-2" },
                                    React.createElement(TooltipProvider, null,
                                        React.createElement(Tooltip, null,
                                            React.createElement(TooltipTrigger, { asChild: true },
                                                React.createElement("button", { className: "flex items-center gap-1 hover:opacity-70" },
                                                    React.createElement("span", null, charge.message),
                                                    React.createElement(Info, { className: "h-4 w-4 text-muted-foreground" }))),
                                            React.createElement(TooltipContent, { side: "right", className: "bg-gray-900 text-white" },
                                                React.createElement("div", { className: "space-y-1" },
                                                    React.createElement("p", { className: "font-semibold" }, "Charge Code"),
                                                    React.createElement("p", null, charge.charge_code || 'N/A'),
                                                    React.createElement("p", { className: "font-semibold mt-2" }, "Charge ID"),
                                                    React.createElement("p", null, charge.charge_id || 'N/A'))))),
                                    !((_b = charge.shipment) === null || _b === void 0 ? void 0 : _b.shipment_id) && (React.createElement(Badge, { className: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" }, "Order-Level")))),
                            React.createElement(TableCell, { className: "text-right font-medium" }, formatMoney(charge.value)),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_c = charge.shipment) === null || _c === void 0 ? void 0 : _c.shipment_id) && ((_d = charge.shipment) === null || _d === void 0 ? void 0 : _d._id) ? (React.createElement(ShipmentExternalLink, { id: (_e = charge.shipment) === null || _e === void 0 ? void 0 : _e._id, label: (_f = charge.shipment) === null || _f === void 0 ? void 0 : _f.shipment_id })) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                            React.createElement(TableCell, null, (_h = (_g = charge.equipment) === null || _g === void 0 ? void 0 : _g.equipment_id) !== null && _h !== void 0 ? _h : NO_DATA),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, charge.created_at ? (React.createElement("span", { className: "text-sm" }, formatDate(charge.created_at, {
                                dateFormat: 'll',
                                timeFormat: 'LT',
                            }))) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                            React.createElement(TableCell, { className: "capitalize whitespace-nowrap" }, charge.rate_source || NO_DATA),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_j = charge.payout_id) === null || _j === void 0 ? void 0 : _j._id) ? (React.createElement(Link, { to: `/payouts/${(_k = charge.payout_id) === null || _k === void 0 ? void 0 : _k._id}`, className: "text-blue-600 hover:underline flex items-center gap-1", target: "_blank", rel: "noopener noreferrer" },
                                ((_l = charge.payout_id) === null || _l === void 0 ? void 0 : _l.number) || ((_m = charge.payout_id) === null || _m === void 0 ? void 0 : _m._id),
                                React.createElement(ExternalLink, { className: "h-3 w-3" }))) : ((_o = charge.payout_id) === null || _o === void 0 ? void 0 : _o.formatted_total) ? (charge.payout_id.formatted_total) : (NO_DATA)),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_p = charge.payout_id) === null || _p === void 0 ? void 0 : _p.timestamp) ? (React.createElement("span", { className: "text-sm" }, formatDate((_q = charge.payout_id) === null || _q === void 0 ? void 0 : _q.timestamp, {
                                dateFormat: 'll',
                                timeFormat: 'LT',
                            }))) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_r = charge.payout_id) === null || _r === void 0 ? void 0 : _r.status) ? (React.createElement(Badge, { className: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" }, titleize(humanize(charge.payout_id.status)))) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                            React.createElement(TableCell, null, ((_s = charge.payout_id) === null || _s === void 0 ? void 0 : _s.paid_date) ? (React.createElement("span", { className: "text-sm" }, formatDate(charge.payout_id.paid_date, {
                                dateFormat: 'll',
                            }))) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                            showOptionalColumns && (React.createElement(React.Fragment, null,
                                React.createElement(TableCell, null, (_t = charge.reason_code) !== null && _t !== void 0 ? _t : NO_DATA),
                                React.createElement(TableCell, null, (_u = charge.notes) !== null && _u !== void 0 ? _u : NO_DATA),
                                React.createElement(TableCell, { className: "text-sm text-muted-foreground" }, (_v = charge.user) !== null && _v !== void 0 ? _v : NO_DATA))),
                            React.createElement(TableCell, null),
                            React.createElement(TableCell, null, (((_w = charge.payout_id) === null || _w === void 0 ? void 0 : _w._id) ||
                                (!((_x = charge.payout_id) === null || _x === void 0 ? void 0 : _x._id) &&
                                    canShowApproveAction(charge) &&
                                    !isApproveDisabled(charge)) ||
                                (onEditCharge &&
                                    canShowEditAction(charge, isFinance)) ||
                                canShowDeleteAction(charge, isFinance)) && (React.createElement(DropdownMenu, null,
                                React.createElement(DropdownMenuTrigger, { asChild: true },
                                    React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0" },
                                        React.createElement(MoreHorizontal, { className: "h-4 w-4" }))),
                                React.createElement(DropdownMenuContent, { align: "end" },
                                    ((_y = charge.payout_id) === null || _y === void 0 ? void 0 : _y._id) && (React.createElement(DropdownMenuItem, { onClick: () => handleViewPayout(charge) },
                                        React.createElement(ExternalLink, { className: "h-4 w-4 mr-2" }),
                                        "View Payout")),
                                    !((_z = charge.payout_id) === null || _z === void 0 ? void 0 : _z._id) &&
                                        canShowApproveAction(charge) &&
                                        !isApproveDisabled(charge) && (React.createElement(DropdownMenuItem, { onClick: () => handleInitiateApprove(charge) },
                                        React.createElement(CheckCheck, { className: "h-4 w-4 mr-2" }),
                                        "Approve")),
                                    onEditCharge &&
                                        canShowEditAction(charge, isFinance) && (React.createElement(DropdownMenuItem, { onClick: () => onEditCharge(charge) },
                                        React.createElement(Edit, { className: "h-4 w-4 mr-2" }),
                                        "Edit")),
                                    canShowDeleteAction(charge, isFinance) && (React.createElement(DropdownMenuItem, { onClick: () => handleInitiateVoid(charge), className: "text-red-600" },
                                        React.createElement(X, { className: "h-4 w-4 mr-2" }),
                                        "Delete"))))))));
                    }),
                !isLoadingAP && filteredCharges.length === 0 && (React.createElement(TableRow, null,
                    React.createElement(TableCell, { colSpan: showOptionalColumns ? 16 : 13, className: "text-center text-muted-foreground py-8" }, apLines.length === 0
                        ? 'No charges found'
                        : 'No charges match the selected filter'))),
                !isLoadingAP ? (React.createElement(TableRow, { className: "bg-muted/50 font-semibold" },
                    React.createElement(TableCell, null),
                    React.createElement(TableCell, null, "Total"),
                    React.createElement(TableCell, { className: "text-right" }, formatMoney(filteredTotal)),
                    React.createElement(TableCell, { colSpan: 10 }))) : (React.createElement(TableRow, { className: "bg-muted/50 font-semibold" },
                    React.createElement(TableCell, null),
                    React.createElement(TableCell, null,
                        React.createElement(Skeleton, { className: "h-4 w-16" })),
                    React.createElement(TableCell, { className: "text-right" },
                        React.createElement(Skeleton, { className: "h-4 w-24 ml-auto" })),
                    React.createElement(TableCell, { colSpan: 10 }))))),
        React.createElement(OrderVendorPayables, { orderId: orderId, enabled: Boolean(orderId) }),
        showPayoutDetailsModal && (React.createElement(CargomaticModal, { title: "", show: showPayoutDetailsModal, footer: null, body: React.createElement(PayoutDetails, { payoutId: (_a = selectedPayoutCharge === null || selectedPayoutCharge === void 0 ? void 0 : selectedPayoutCharge.payout_id) === null || _a === void 0 ? void 0 : _a._id }), onHide: () => {
                setShowPayoutDetailsModal(false);
                setSelectedPayoutCharge(null);
            }, dialogClassName: "modal-override" })),
        React.createElement(ConfirmModal, { dataTestId: "confirm-void-modal", show: showConfirmVoidModal, onHide: () => {
                setShowConfirmVoidModal(false);
                setChargeToVoid(null);
            }, onConfirm: handleConfirmVoid, title: "Confirm Delete", message: "Are you sure you want to delete this charge? This action cannot be undone.", affirmative: voidPending ? 'Deleting...' : 'Delete', primaryDisabled: voidPending, primaryButtonColor: "red" }),
        React.createElement(ConfirmModal, { dataTestId: "confirm-approve-modal", show: showConfirmApproveModal, onHide: () => {
                setShowConfirmApproveModal(false);
                setChargeToApprove(null);
            }, onConfirm: handleConfirmApprove, title: "Confirm Approval", message: "Are you sure you want to approve this charge?", affirmative: approvePending ? 'Approving...' : 'Approve', primaryDisabled: approvePending }),
        React.createElement(GeneratePayoutDialog, { open: payoutModalOpen, onClose: () => setPayoutModalOpen(false), onSuccess: handlePayoutSuccess, orderId: orderId, charges: unactionedCharges, initialSelectedIds: selectedCharges }),
        React.createElement(RerateCostsDialog, { open: showRerateDialog, onOpenChange: setShowRerateDialog, charges: rerateEligibleCharges, onRerate: handleRerate, isPending: rerateMutation.isPending })));
}
export default APChargesTable;
